import axios from 'axios';
import { useAuthStore } from '@/hooks/useAuthStore';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ─────────────────────────────────────────────────────────────────────────────
// C14-E.6 httpOnly cookies: `withCredentials: true` is required so the browser
// attaches the HttpOnly `refreshToken` cookie on /auth/refresh and /auth/logout
// (same-origin in prod behind the admin reverse proxy; same-site in dev).
// Without this flag axios would drop the cookie from outgoing XHRs.
// ─────────────────────────────────────────────────────────────────────────────
export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ─────────────────────────────────────────────────────────────────────────────
// C14-E.6 httpOnly cookies: the refresh token is no longer stored in JS/
// localStorage — it lives in an HttpOnly cookie emitted by the admin-api at
// login / previous refresh. The interceptor therefore:
//   1. posts to /auth/refresh with an EMPTY body (backend reads req.cookies),
//   2. sets `withCredentials: true` on this raw axios call so the cookie is
//      sent even though we bypass the pre-configured instance,
//   3. receives only `{ accessToken }` in the body (refresh token rotation is
//      invisible: a new cookie is returned via Set-Cookie and stored by the
//      browser automatically).
// ─────────────────────────────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const { accessToken } = response.data.data;
        useAuthStore.getState().setAccessToken(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed (no cookie, expired, revoked) — clear the store so
        // ProtectedRoute sends the user back to /login.
        useAuthStore.getState().logout();
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
// ─────────────────────────────────────────────────────────────────────────
// D41 (C8): `/auth/me` now returns mfaEnabled + mfaVerified directly from the
// DB, so the admin-web no longer needs to probe /mfa/setup to infer state.
// D42 (C8): `disableMfa` is a new self-service endpoint that requires the
// current password + a valid TOTP code.  On success the backend revokes all
// refresh tokens, so the UI must redirect to /login.
// ─────────────────────────────────────────────────────────────────────────

export type MeResponse = {
  success: boolean;
  data: {
    staff: {
      id: string;
      email: string;
      nombre: string;
      role: string;
      /** D41: true once the staff completed setup/verify at least once. */
      mfaEnabled: boolean;
      /**
       * D41: true when the staff is in a fully-verified state (enabled and
       * has confirmed a TOTP code during enrollment).  `mfaEnabled &&
       * !mfaVerified` is the "limbo" state that the C8 cron resets.
       */
      mfaVerified: boolean;
      /**
       * C9 A.6: remaining unused MFA backup codes.  `undefined` when the API
       * has not yet deployed the backup-codes feature — treat as unknown and
       * fall back to hiding the "remaining" count.
       */
      backupCodesRemaining?: number;
      activo?: boolean;
      lastLoginAt?: string | null;
    };
  };
};

export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/api/v1/auth/login', { email, password });
    return response.data;
  },

  verifyMfa: async (tempToken: string, code: string) => {
    const response = await api.post('/api/v1/auth/mfa/verify', { tempToken, code });
    return response.data;
  },

  getMe: async (): Promise<MeResponse> => {
    const response = await api.get('/api/v1/auth/me');
    return response.data;
  },

  // C14-E.6 httpOnly: body is now empty; admin-api reads `refreshToken` from
  // the HttpOnly cookie and clears it with Set-Cookie/Max-Age=0. Callers no
  // longer need a refreshToken reference — this signature keeps the call-site
  // ergonomic (Layout.tsx calls `authApi.logout()` with no args).
  logout: async () => {
    const response = await api.post('/api/v1/auth/logout', {});
    return response.data;
  },

  // C7-6: MFA enrollment endpoints (authenticated staff self-service)
  setupMfa: async (): Promise<{ success: boolean; data?: { qrCodeUrl: string; secret: string } }> => {
    const response = await api.post('/api/v1/auth/mfa/setup');
    return response.data;
  },

  // C9 A.6: `/auth/mfa/setup/verify` now returns `backupCodes` (10 plaintext
  // single-shot codes) alongside the success message.  The UI MUST display
  // them once and then drop the reference — the backend hashes them with
  // bcrypt and will never return them again.
  verifyMfaSetup: async (
    code: string
  ): Promise<{
    success: boolean;
    data?: { message: string; backupCodes?: string[] };
  }> => {
    const response = await api.post('/api/v1/auth/mfa/setup/verify', { code });
    return response.data;
  },

  // D42 (C8) + C9 A.6: self-service MFA disable. Requires current password +
  // EITHER a valid TOTP code OR an unused backup code (but not both).  The
  // backup-code branch lets a staff who lost their authenticator device still
  // disable MFA without contacting a SUPER_ADMIN — mitigates the D42 auto-lock
  // single-admin scenario.
  //
  // Backend returns 200 on success and revokes refresh tokens so the caller
  // MUST redirect to /login after awaiting this promise.
  disableMfa: async (
    password: string,
    opts: { totpCode?: string; backupCode?: string }
  ): Promise<{ success: boolean; data?: { message: string } }> => {
    const response = await api.post('/api/v1/auth/mfa/disable', {
      password,
      ...(opts.totpCode ? { totpCode: opts.totpCode } : {}),
      ...(opts.backupCode ? { backupCode: opts.backupCode } : {}),
    });
    return response.data;
  },

  // C9 A.6: regenerate MFA backup codes.  Drops every unused code on the
  // server and returns 10 new plaintext codes (single-shot).  Requires
  // password + TOTP re-auth (pattern D42).  The UI MUST show the codes once
  // and warn the user to save them before closing the dialog.
  regenerateBackupCodes: async (
    password: string,
    totpCode: string
  ): Promise<{ codes: string[] }> => {
    const response = await api.post('/api/v1/auth/mfa/backup-codes/regenerate', {
      password,
      totpCode,
    });
    return response.data.data;
  },
};

// Audit API
export const auditApi = {
  getLogs: async (params?: {
    limit?: number;
    offset?: number;
    action?: string;
    staffId?: string;
    targetType?: string;
    targetId?: string;
    severity?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const response = await api.get('/api/v1/audit/logs', { params });
    return response.data;
  },

  verifyIntegrity: async (params?: { fromDate?: string; toDate?: string }) => {
    const response = await api.get('/api/v1/audit/verify', { params });
    return response.data;
  },

  getActions: async () => {
    const response = await api.get('/api/v1/audit/actions');
    return response.data;
  },
};

// Staff API
export const staffApi = {
  getAll: async (params?: {
    search?: string;
    role?: string;
    activo?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/api/v1/staff', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v1/staff/${id}`);
    return response.data;
  },

  create: async (data: {
    email: string;
    password: string;
    nombre: string;
    role: string;
  }) => {
    const response = await api.post('/api/v1/staff', data);
    return response.data;
  },

  update: async (id: string, data: {
    nombre?: string;
    role?: string;
    activo?: boolean;
  }) => {
    const response = await api.patch(`/api/v1/staff/${id}`, data);
    return response.data;
  },

  resetPassword: async (id: string, newPassword: string) => {
    const response = await api.post(`/api/v1/staff/${id}/reset-password`, { newPassword });
    return response.data;
  },

  unlock: async (id: string) => {
    const response = await api.post(`/api/v1/staff/${id}/unlock`);
    return response.data;
  },

  disableMfa: async (id: string) => {
    const response = await api.post(`/api/v1/staff/${id}/disable-mfa`);
    return response.data;
  },

  deactivate: async (id: string) => {
    const response = await api.post(`/api/v1/staff/${id}/deactivate`);
    return response.data;
  },
};

// Users API
export const usersApi = {
  getAll: async (params?: {
    search?: string;
    activo?: boolean;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/api/v1/users', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v1/users/${id}`);
    return response.data;
  },

  getActivity: async (id: string, limit?: number) => {
    const response = await api.get(`/api/v1/users/${id}/activity`, { params: { limit } });
    return response.data;
  },

  suspend: async (id: string, reason: string) => {
    const response = await api.post(`/api/v1/users/${id}/suspend`, { reason });
    return response.data;
  },

  reactivate: async (id: string) => {
    const response = await api.post(`/api/v1/users/${id}/reactivate`);
    return response.data;
  },

  delete: async (id: string, type: 'soft' | 'hard', reason: string) => {
    const response = await api.post(`/api/v1/users/${id}/delete`, { type, reason });
    return response.data;
  },

  restore: async (id: string) => {
    const response = await api.post(`/api/v1/users/${id}/restore`);
    return response.data;
  },
};

// Households API
export const householdsApi = {
  getAll: async (params?: {
    search?: string;
    limit?: number;
    offset?: number;
  }) => {
    const response = await api.get('/api/v1/households', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v1/households/${id}`);
    return response.data;
  },

  getStats: async (id: string) => {
    const response = await api.get(`/api/v1/households/${id}/stats`);
    return response.data;
  },
};

// Overview API
export const overviewApi = {
  getStats: async () => {
    const response = await api.get('/api/v1/overview/stats');
    return response.data;
  },

  getActivity: async () => {
    const response = await api.get('/api/v1/overview/activity');
    return response.data;
  },

  getActivityByDay: async (days?: number) => {
    const response = await api.get('/api/v1/overview/activity-by-day', { params: { days } });
    return response.data;
  },

  getSecurity: async () => {
    const response = await api.get('/api/v1/overview/security');
    return response.data;
  },
};

// B2: Product Metrics API
export const productMetricsApi = {
  getFunnel: async (days?: number) => {
    const response = await api.get('/api/v1/product-metrics/funnel', { params: { days } });
    return response.data;
  },

  getCohorts: async (weeks?: number) => {
    const response = await api.get('/api/v1/product-metrics/cohorts', { params: { weeks } });
    return response.data;
  },

  getCompleteness: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/api/v1/product-metrics/completeness', { params });
    return response.data;
  },

  getFeatures: async () => {
    const response = await api.get('/api/v1/product-metrics/features');
    return response.data;
  },

  getSummary: async () => {
    const response = await api.get('/api/v1/product-metrics/summary');
    return response.data;
  },
};

// Import Jobs API
export const importJobsApi = {
  getAll: async (params?: {
    limit?: number;
    offset?: number;
    status?: string;
    hogarId?: string;
    fromDate?: string;
    toDate?: string;
  }) => {
    const response = await api.get('/api/v1/import-jobs', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/v1/import-jobs/stats');
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/api/v1/import-jobs/${id}`);
    return response.data;
  },
};

// Feature Flags API
export const featureFlagsApi = {
  getAll: async (category?: string) => {
    const response = await api.get('/api/v1/feature-flags', { params: { category } });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/v1/feature-flags/stats');
    return response.data;
  },

  get: async (key: string) => {
    const response = await api.get(`/api/v1/feature-flags/${key}`);
    return response.data;
  },

  toggle: async (key: string, enabled: boolean) => {
    const response = await api.post(`/api/v1/feature-flags/${key}/toggle`, { enabled });
    return response.data;
  },

  create: async (data: {
    key: string;
    name: string;
    description: string;
    enabled: boolean;
    category: string;
  }) => {
    const response = await api.post('/api/v1/feature-flags', data);
    return response.data;
  },

  update: async (key: string, data: {
    name?: string;
    description?: string;
    enabled?: boolean;
    category?: string;
  }) => {
    const response = await api.patch(`/api/v1/feature-flags/${key}`, data);
    return response.data;
  },

  delete: async (key: string) => {
    const response = await api.delete(`/api/v1/feature-flags/${key}`);
    return response.data;
  },
};

// Break-glass API
export const breakglassApi = {
  getActions: async () => {
    const response = await api.get('/api/v1/breakglass/actions');
    return response.data;
  },

  getHistory: async (params?: { limit?: number; offset?: number }) => {
    const response = await api.get('/api/v1/breakglass/history', { params });
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/api/v1/breakglass/stats');
    return response.data;
  },
};

// AI Ops API
export const aiOpsApi = {
  getStats: async () => {
    const response = await api.get('/api/v1/ai-ops/stats');
    return response.data;
  },

  getUsageByDay: async (days?: number) => {
    const response = await api.get('/api/v1/ai-ops/usage-by-day', { params: { days } });
    return response.data;
  },

  getTopUsers: async (limit?: number) => {
    const response = await api.get('/api/v1/ai-ops/top-users', { params: { limit } });
    return response.data;
  },

  getRecentSessions: async (limit?: number) => {
    const response = await api.get('/api/v1/ai-ops/recent-sessions', { params: { limit } });
    return response.data;
  },

  getSessionDetails: async (id: string) => {
    const response = await api.get(`/api/v1/ai-ops/sessions/${id}`);
    return response.data;
  },
};

// Settings API
export const settingsApi = {
  getByCategory: async (category: string) => {
    const response = await api.get(`/api/v1/settings/${category}`);
    return response.data;
  },

  updateMany: async (settings: { key: string; value: string }[]) => {
    const response = await api.put('/api/v1/settings', { settings });
    return response.data;
  },

  update: async (key: string, value: string) => {
    const response = await api.patch(`/api/v1/settings/${key}`, { value });
    return response.data;
  },

  testSmtp: async () => {
    const response = await api.post('/api/v1/settings/smtp/test');
    return response.data;
  },
};

// ─────────────────────────────────────────────────────────────────────────
// C9 B.5: Admin Integrations API
// ─────────────────────────────────────────────────────────────────────────
// SUPER_ADMIN-only module to manage infra-level secrets (GitHub Actions PAT
// consumed by the CT110 exporter, Slack webhook).  Every mutation requires
// TOTP re-auth (pattern D42) and emits CRITICAL audit events.  Values are
// AES-256-GCM encrypted at rest.  The list endpoint only exposes a
// sha256-derived hint (no plaintext ever leaves the backend via /integrations
// — the service-token `/value` endpoint handles exporter reads).
// ─────────────────────────────────────────────────────────────────────────

export type IntegrationKind = 'GITHUB_ACTIONS_PAT' | 'SLACK_WEBHOOK';

export interface IntegrationDTO {
  kind: IntegrationKind | string;
  valueHint: string;
  enabled: boolean;
  updatedAt: string;
  updatedBy: string;
}

export const integrationsApi = {
  list: async (): Promise<IntegrationDTO[]> => {
    const response = await api.get('/api/v1/integrations');
    return response.data.data.integrations as IntegrationDTO[];
  },

  upsert: async (
    kind: string,
    value: string,
    password: string,
    totpCode: string
  ): Promise<void> => {
    await api.put(`/api/v1/integrations/${kind}`, { value, password, totpCode });
  },

  delete: async (kind: string, password: string, totpCode: string): Promise<void> => {
    await api.delete(`/api/v1/integrations/${kind}`, {
      data: { password, totpCode },
    });
  },
};
